import { useTranslation } from '@/hooks/use-translation';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAllo, $fetchAllo } from '@/services/api-allo/client';
import type { InfiniteData, Query } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

type Contact = components['schemas']['Contact'];
type ContactsResponse = {
  data?: Contact[];
  metadata?: {
    pagination?: { current_page?: number; total_pages?: number };
  };
};

// Helper to match filter queries
const isFilterQuery = (query: Query): boolean =>
  Array.isArray(query.queryKey) &&
  query.queryKey[0] === 'contacts' &&
  query.queryKey[1] === 'filter';

// Mutation context type for optimistic updates with rollback
type MutationContext = {
  previousData: [unknown[], InfiniteData<ContactsResponse> | undefined][];
  previousFilterData: [unknown[], InfiniteData<ContactsResponse> | undefined][];
};

interface UseCreateContactMutationOptions {
  onSuccess?: (contact: Contact) => void;
  onError?: () => void;
}

/**
 * Hook to create a new contact.
 * Optimistically adds the contact to the cache.
 */
export function useCreateContactMutation({
  onSuccess,
  onError,
}: UseCreateContactMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  return $apiAllo.useMutation('post', '/v1/contact', {
    onSuccess: async (newContact) => {
      await queryClient.cancelQueries({ queryKey: ['get', '/v1/contact'] });
      await queryClient.cancelQueries({ predicate: isFilterQuery });

      const newContactWithLabel = {
        ...newContact.data,
        isNewAddedContact: true,
      };

      // Update regular contacts cache
      queryClient.setQueriesData<InfiniteData<ContactsResponse>>(
        { queryKey: ['get', '/v1/contact'] },
        (oldData) => {
          if (!oldData || !oldData.pages?.length) {
            return oldData;
          }

          const firstPage = oldData.pages[0];
          if (!firstPage) {
            return oldData;
          }

          return {
            ...oldData,
            pages: [
              { ...firstPage, data: [newContactWithLabel, ...(firstPage.data ?? [])] },
              ...oldData.pages.slice(1),
            ],
          };
        },
      );

      // Update filter queries cache
      queryClient.setQueriesData<InfiniteData<ContactsResponse>>(
        { predicate: isFilterQuery },
        (oldData) => {
          if (!oldData || !oldData.pages?.length) {
            return oldData;
          }

          const firstPage = oldData.pages[0];
          if (!firstPage) {
            return oldData;
          }

          return {
            ...oldData,
            pages: [
              { ...firstPage, data: [newContactWithLabel, ...(firstPage.data ?? [])] },
              ...oldData.pages.slice(1),
            ],
          };
        },
      );

      toast.success(t('components.contacts.form.createContactSuccess'));

      onSuccess?.(newContact.data as Contact);
    },
    onError: () => {
      toast.error(t('components.contacts.form.createContactError'));
      onError?.();
    },
  });
}

interface UseDeleteContactMutationOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

/**
 * Hook to delete a contact by ID.
 * Optimistically removes the contact from the cache.
 */
export function useDeleteContactMutation({
  onSuccess,
  onError,
}: UseDeleteContactMutationOptions = {}) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  return $apiAllo.useMutation('delete', '/v1/contact/{contact_id}', {
    onMutate: async (variables) => {
      const contactId = variables.params.path.contact_id;

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['get', '/v1/contact'] });
      await queryClient.cancelQueries({ predicate: isFilterQuery });

      // Snapshot previous data for rollback
      const previousData = queryClient.getQueriesData<InfiniteData<ContactsResponse>>({
        queryKey: ['get', '/v1/contact'],
      });
      const previousFilterData = queryClient.getQueriesData<InfiniteData<ContactsResponse>>({
        predicate: isFilterQuery,
      });

      // Helper to remove contact from pages
      const removeContact = (oldData: InfiniteData<ContactsResponse> | undefined) => {
        if (!oldData?.pages?.length) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data?.filter((contact) => contact.id !== contactId),
          })),
        };
      };

      // Optimistically remove from regular contacts cache
      queryClient.setQueriesData<InfiniteData<ContactsResponse>>(
        { queryKey: ['get', '/v1/contact'] },
        removeContact,
      );

      // Optimistically remove from filter queries cache
      queryClient.setQueriesData<InfiniteData<ContactsResponse>>(
        { predicate: isFilterQuery },
        removeContact,
      );

      return { previousData, previousFilterData };
    },
    onSuccess: () => {
      toast.success(t('components.contacts.form.deleteContactSuccess'));
      onSuccess?.();
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      const ctx = context as MutationContext | undefined;
      if (ctx?.previousData) {
        for (const [queryKey, data] of ctx.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (ctx?.previousFilterData) {
        for (const [queryKey, data] of ctx.previousFilterData) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      console.error('Error deleting contact:', error);
      toast.error(t('components.contacts.form.deleteContactError'));
      onError?.();
    },
  });
}

interface UseBlockContactMutationOptions {
  contactId: string | null | undefined;
  onSuccess?: () => void;
  onError?: () => void;
}

/**
 * Hook to block a contact by blocking all their phone numbers.
 * Optimistically removes the contact from the cache.
 */
export function useBlockContactMutation({
  contactId,
  onSuccess,
  onError,
}: UseBlockContactMutationOptions) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (phoneNumbers: string[]) => {
      const results = await Promise.all(
        phoneNumbers.map((number) =>
          $fetchAllo.POST('/v1/business/contacts/rules', {
            body: {
              from_number: number.replaceAll(/[^\d+]/g, ''),
              action: 'BLOCK',
            },
          }),
        ),
      );

      const hasError = results.some((r) => r.error);
      if (hasError) {
        throw new Error('Failed to block contact');
      }

      return results;
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['get', '/v1/contact'] });
      await queryClient.cancelQueries({ predicate: isFilterQuery });

      // Snapshot previous data for rollback
      const previousData = queryClient.getQueriesData<InfiniteData<ContactsResponse>>({
        queryKey: ['get', '/v1/contact'],
      });
      const previousFilterData = queryClient.getQueriesData<InfiniteData<ContactsResponse>>({
        predicate: isFilterQuery,
      });

      // Helper to remove contact from pages
      const removeContact = (oldData: InfiniteData<ContactsResponse> | undefined) => {
        if (!oldData?.pages?.length) {
          return oldData;
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: page.data?.filter((contact) => contact.id !== contactId),
          })),
        };
      };

      // Optimistically remove from regular contacts cache
      queryClient.setQueriesData<InfiniteData<ContactsResponse>>(
        { queryKey: ['get', '/v1/contact'] },
        removeContact,
      );

      // Optimistically remove from filter queries cache
      queryClient.setQueriesData<InfiniteData<ContactsResponse>>(
        { predicate: isFilterQuery },
        removeContact,
      );

      onSuccess?.();

      return { previousData, previousFilterData };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get', '/v1/business/contacts/rules'] });
      toast.success(t('components.contacts.menu.blockContactSuccess', 'Contact blocked'));
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousFilterData) {
        for (const [queryKey, data] of context.previousFilterData) {
          queryClient.setQueryData(queryKey, data);
        }
      }

      console.error('Error blocking contact:', error);
      toast.error(t('components.contacts.menu.blockContactError', 'Failed to block contact'));
      onError?.();
    },
  });
}

interface UseUnblockContactMutationOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

/**
 * Hook to unblock a contact by removing BLOCK rules for all their phone numbers.
 */
export function useUnblockContactMutation({
  onSuccess,
  onError,
}: UseUnblockContactMutationOptions) {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (phoneNumbers: string[]) => {
      const results = await Promise.all(
        phoneNumbers.map((number) =>
          $fetchAllo.DELETE('/v1/business/contacts/rules', {
            body: {
              from_number: number.replaceAll(/[^\d+]/g, ''),
            },
          }),
        ),
      );

      const hasError = results.some((r) => r.error);
      if (hasError) {
        throw new Error('Failed to unblock contact');
      }

      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get', '/v1/business/contacts/rules'] });
      toast.success(t('components.contacts.menu.unblockContactSuccess', 'Contact unblocked'));
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Error unblocking contact:', error);
      toast.error(t('components.contacts.menu.unblockContactError', 'Failed to unblock contact'));
      onError?.();
    },
  });
}

// Mutation context type for favorite toggle with single contact rollback
type FavoriteMutationContext = {
  previousData: [unknown[], InfiniteData<ContactsResponse> | undefined][];
  previousFilterData: [unknown[], InfiniteData<ContactsResponse> | undefined][];
  previousContact: unknown;
};

/**
 * Hook to toggle a contact's favorite (whitelisted) status.
 * Uses POST /v1/business/contacts/rules with action ALLOW to add,
 * DELETE /v1/business/contacts/rules to remove.
 */
export function useToggleFavoriteMutation() {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      phoneNumbers,
      isFavorite,
    }: {
      contactId: string;
      phoneNumbers: string[];
      isFavorite: boolean;
    }) => {
      const results = await Promise.all(
        phoneNumbers.map((number) => {
          const cleanNumber = number.replaceAll(/[^\d+]/g, '');
          return isFavorite
            ? $fetchAllo.DELETE('/v1/business/contacts/rules', {
                body: { from_number: cleanNumber },
              })
            : $fetchAllo.POST('/v1/business/contacts/rules', {
                body: { from_number: cleanNumber, action: 'ALLOW' },
              });
        }),
      );

      const hasError = results.some((r) => r.error);
      if (hasError) {
        throw new Error('Failed to toggle favorite');
      }

      return results;
    },
    onMutate: async ({ contactId, isFavorite }) => {
      await queryClient.cancelQueries({ queryKey: ['get', '/v1/contact'] });
      await queryClient.cancelQueries({ predicate: isFilterQuery });

      const previousData = queryClient.getQueriesData<InfiniteData<ContactsResponse>>({
        queryKey: ['get', '/v1/contact'],
      });
      const previousFilterData = queryClient.getQueriesData<InfiniteData<ContactsResponse>>({
        predicate: isFilterQuery,
      });
      const previousContact = queryClient.getQueryData([
        'get',
        '/v1/contact/{contact_id}',
        { params: { path: { contact_id: contactId } } },
      ]);

      const newWhitelistedValue = !isFavorite;

      const updateContact = (contact: Contact): Contact => {
        if (contact.id !== contactId) {
          return contact;
        }
        return { ...contact, is_whitelisted: newWhitelistedValue };
      };

      // Update contacts list cache
      queryClient.setQueriesData<InfiniteData<ContactsResponse>>(
        { queryKey: ['get', '/v1/contact'] },
        (oldData) => {
          if (!oldData?.pages?.length) {
            return oldData;
          }
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map(updateContact),
            })),
          } as any;
        },
      );

      // Update filter queries cache
      queryClient.setQueriesData<InfiniteData<ContactsResponse>>(
        { predicate: isFilterQuery },
        (oldData) => {
          if (!oldData?.pages?.length) {
            return oldData;
          }
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data?.map(updateContact),
            })),
          };
        },
      );

      // Update single contact query
      queryClient.setQueryData(
        ['get', '/v1/contact/{contact_id}', { params: { path: { contact_id: contactId } } }],
        (oldData: any) => {
          if (!oldData?.data) {
            return oldData;
          }
          return { ...oldData, data: { ...oldData.data, is_whitelisted: newWhitelistedValue } };
        },
      );

      return { previousData, previousFilterData, previousContact };
    },
    onSuccess: (_data, { contactId, isFavorite }) => {
      analytics.track(ANALYTICS_EVENTS.FAVORITES.CLICKED, {
        contact_id: contactId,
        action: isFavorite ? 'removed' : 'added',
      });
      toast.success(
        isFavorite
          ? t('components.contacts.favorite.removedSuccess', 'Removed from favorites')
          : t('components.contacts.favorite.addedSuccess', 'Added to favorites'),
      );
    },
    onError: (_error, { contactId }, context) => {
      const ctx = context as FavoriteMutationContext | undefined;
      if (ctx?.previousData) {
        for (const [queryKey, data] of ctx.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (ctx?.previousFilterData) {
        for (const [queryKey, data] of ctx.previousFilterData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (ctx?.previousContact) {
        queryClient.setQueryData(
          ['get', '/v1/contact/{contact_id}', { params: { path: { contact_id: contactId } } }],
          ctx.previousContact,
        );
      }

      toast.error(t('components.contacts.favorite.error', 'Failed to update favorite'));
    },
    onSettled: (_data, _error, { contactId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          'get',
          '/v1/contact/{contact_id}',
          { params: { path: { contact_id: contactId } } },
        ],
      });
      queryClient.invalidateQueries({ queryKey: ['get', '/v1/contact'] });
    },
  });
}
